import React, { useEffect } from 'react';
import { useTheme } from '@emotion/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mdiRefresh } from '@mdi/js';
import Icon from '@mdi/react';
import MyDayPlanMealsCard from './ my_day_plan_meals_card';
import {
  formatLongDateInLocale,
  calendarDayFromJSDate,
} from '../../services/dates';
import MyDayWelcomeVideoCard from './my_day_welcome_video_card';
import MyDayLetsStartCardButton from './my_day_lets_start_card_button';
import MyDayContentCard from './my_day_content_card';
import { PlannerEntryType, SpacePublishedSectionType } from '../../API';
import MyDayLatestCollectionsSection from './my_day_latest_collections_section';
import MyDayPublishedContentLaneSection from './my_day_published_content_lane_section';
import MyDayTodaysMealsSection from './my_day_todays_meals_section';
import MyDayTodaysLessonsSection from './my_day_todays_lessons_section';
import MyDayShoppingListsSection from './my_day_shopping_lists_section';
import { useIsMobile } from '../common/layout_hooks';
import { isEmbeddedInSmorgCompanion } from '../../services/smorg_companion_auth';
import SmartBanner from '../common/smartbanner/SmartBanner';
import {
  useGetDbPlannerWeekQuery,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useGetSharedProgrammeQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { userCountryCode } from '../../services/users';
import {
  dbWeekStartDateStr,
  plannerEntriesInCalendarDay,
} from '../../services/planner';
import {
  getContentEntriesForCalendarDay,
  isContentEntryWatched,
  isDateWithinEnrollmentPeriod,
  isProgrammeEnrollmentCompleted,
  programmeStartCalendarDay,
} from '../../services/my_day';
import MyDayProgrammeCompletedCard from './ my_day_programme_completed_card';
import {
  mergeMyDayActionRecords,
  sharedProgrammeDurationDays,
} from '../../services/programmes';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetEndUserSpaceQuery,
  useGetSharedProgrammesForEndUserQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { refreshAppPreservingBackgroundColor } from '../../services/detect_app_update';
import { useTracker } from '../../operations/tracking_operations';
import { SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';
import { SPACE_ONBOARDING_AFTER_SIGNUP_ROUTE } from '../../services/routes';
import {
  hasUserProvidedEnoughOnboardingAnswers,
  isSpaceOnboardingCompleted,
} from '../../services/end_user_space_memberships';

/**
 * Segments of the My Day page, in order:
 * - Welcome video, only if not enrolled into a program
 * - Prompt to plan meals for today
 * - Today's Meals
 * - Today's Lessons
 * - Health pro's content e.g. 'Latest news'
 */
const MyDayView = () => {
  const theme = useTheme();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const tracker = useTracker();

  const { data: userProfile } = useGetUserProfileQuery();

  useEffect(() => {
    console.log(
      `useEffect tracking My Day screen clicked, tracker = ${tracker}`,
    );
    tracker(userProfile, ['My Day screen clicked']);
    // tracker() will mutate userProfile, causing a render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracker, !!userProfile]);

  const {
    data: currentSpaceMembership,
    isLoading: spaceMembershipLoading,
    error: spaceMembershipLoadError,
  } = useGetCurrentSpaceMembershipQuery();

  const { data: currentSpace } = useGetEndUserSpaceQuery();

  const backgroundImageUrl =
    currentSpace?.whiteLabelElements?.myDayBackgroundImageUrl;

  const desktopBannerImageUrl =
    currentSpace?.whiteLabelElements?.desktopBannerImageUrl;

  const backgroundColor = currentSpace?.whiteLabelElements?.backgroundColor;

  const isOnboardingCompleted =
    spaceMembershipLoadError ||
    spaceMembershipLoading ||
    !currentSpaceMembership ||
    isSpaceOnboardingCompleted(currentSpaceMembership) ||
    hasUserProvidedEnoughOnboardingAnswers(currentSpaceMembership);

  // const isInitialProgrammeSelected =
  //   spaceMembershipLoadError ||
  //   spaceMembershipLoading ||
  //   isInitialProgrammeSelectionCompleted(currentSpaceMembership);

  const todayOverride = searchParams.get('asOf');

  const today = todayOverride || calendarDayFromJSDate(new Date());

  const countryCode = userCountryCode(userProfile);

  const dbWeekStartDate = dbWeekStartDateStr(today);

  const { data: dbPlannerWeek } = useGetDbPlannerWeekQuery(dbWeekStartDate);

  const isZeroMealsPlannedToday = !plannerEntriesInCalendarDay(
    dbPlannerWeek,
    today,
  ).some(
    (entry) =>
      !entry.plannerEntryType ||
      entry.plannerEntryType === PlannerEntryType.MEAL,
  );

  const welcomeVideo = currentSpace?.welcomeVideo;

  const welcomeVideoWatched = currentSpaceMembership?.welcomeVideoWatched;

  const showWelcomeVideoCard = !!welcomeVideo && !welcomeVideoWatched;

  const {
    data: currentProgrammeEnrollment,
    error: enrollmentLoadError,
    isLoading: enrollmentLoading,
  } = useCurrentProgrammeEnrollmentQuery();

  const programmeStartDay = programmeStartCalendarDay(
    currentProgrammeEnrollment,
  );

  const { sharedProgrammeID } = currentProgrammeEnrollment || {};

  const { data: sharedProgramme } = useGetSharedProgrammeQuery(
    sharedProgrammeID,
    {
      skip: !sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const { data: myDayActionRecordsByEnrollment } =
    useGetMyDayActionRecordsByEnrollmentQuery(currentProgrammeEnrollment?.id, {
      skip: !currentProgrammeEnrollment?.id,
    });

  const { data: myDayActionRecordsBySpaceMembership } =
    useGetMyDayActionRecordsBySpaceMembershipQuery(currentSpaceMembership?.id, {
      skip: !currentSpaceMembership?.id,
    });

  const isUserEnrolled =
    !enrollmentLoadError && !enrollmentLoading && !!currentProgrammeEnrollment;

  const isEnrollmentCompleted =
    isUserEnrolled &&
    isProgrammeEnrollmentCompleted(
      currentProgrammeEnrollment,
      sharedProgrammeDurationDays(sharedProgramme),
      today,
    );

  const todaysProgrammeEntriesOfTypeContentFromSharedProgramme =
    programmeStartDay &&
    sharedProgramme &&
    getContentEntriesForCalendarDay(sharedProgramme, programmeStartDay, today);

  const myDayActionRecords = mergeMyDayActionRecords(
    myDayActionRecordsBySpaceMembership || [],
    myDayActionRecordsByEnrollment || [],
  );

  const featuredContentProgrammeEntry = (
    todaysProgrammeEntriesOfTypeContentFromSharedProgramme || []
  ).find(
    (programmeEntry) =>
      !isContentEntryWatched(
        programmeEntry.objectID,
        currentProgrammeEnrollment?.id,
        myDayActionRecords || [],
      ),
  );

  const isWithinEnrollmentTimePeriod = isDateWithinEnrollmentPeriod(
    currentProgrammeEnrollment,
    sharedProgramme,
    today,
  );

  const {
    data: allSharedProgrammes,
    error: allSharedProgrammesLoadError,
    isLoading: isAllSharedProgrammesLoading,
  } = useGetSharedProgrammesForEndUserQuery();

  const anyProgrammesAvailable =
    !allSharedProgrammesLoadError &&
    !isAllSharedProgrammesLoading &&
    (!allSharedProgrammes || allSharedProgrammes.length === 0);

  useEffect(() => {
    if (!isOnboardingCompleted) {
      console.log('Navigating to onboarding after signup');
      navigate(SPACE_ONBOARDING_AFTER_SIGNUP_ROUTE);
    }
  }, [navigate, isOnboardingCompleted]);

  const myDayScreenSections = currentSpace?.myDayScreenSections || [];

  const isMobile = useIsMobile();

  const isSmorgCompanion = isEmbeddedInSmorgCompanion();

  return (
    <div className="my-day-view">
      {isMobile && !isSmorgCompanion && <SmartBanner title="Smorg" />}
      <div className="topNavBarMobile" style={{ height: '70px' }}>
        <div style={{ flexDirection: 'column', flexGrow: 1 }}>
          <div className="screen-title">My Day</div>
          <div className="screen-subtitle">
            {formatLongDateInLocale(today, countryCode)}
          </div>
        </div>
        <div>
          <Icon
            path={mdiRefresh}
            style={{
              width: '32px',
              height: '32px',
              marginRight: '18px',
              marginTop: '18px',
            }}
            onClick={() => {
              refreshAppPreservingBackgroundColor(backgroundColor);
            }}
          />
        </div>
      </div>
      <div>
        {backgroundImageUrl && (
          <div
            className="myDayHeroImage"
            style={{
              backgroundImage: `url("${backgroundImageUrl}")`,
            }}>
            &nbsp;
          </div>
        )}
        {desktopBannerImageUrl && (
          <div
            className="myDayDesktopBannerImage"
            style={{
              backgroundImage: `url("${desktopBannerImageUrl}")`,
            }}>
            &nbsp;
          </div>
        )}

        {showWelcomeVideoCard && (
          <MyDayWelcomeVideoCard
            videoPlaylistItem={welcomeVideo}
            cardBackgroundColor={theme.palette.primary.cardBackground}
          />
        )}
        {isUserEnrolled && featuredContentProgrammeEntry && (
          <MyDayContentCard
            contentEntryID={featuredContentProgrammeEntry.objectID}
            parentID={sharedProgrammeID}
            calendarDay={today}
            showMarkAsCompleted
          />
        )}
        {anyProgrammesAvailable &&
          !enrollmentLoadError &&
          !enrollmentLoading &&
          !currentProgrammeEnrollment && <MyDayLetsStartCardButton />}
        {isZeroMealsPlannedToday &&
          isUserEnrolled &&
          isWithinEnrollmentTimePeriod && (
            <MyDayPlanMealsCard
              cardBackgroundColor={theme.palette.primary.cardBackground}
              primaryMainColor={theme.palette.primary.main}
            />
          )}
        {isUserEnrolled && isEnrollmentCompleted && (
          <MyDayProgrammeCompletedCard
            cardBackgroundColor={theme.palette.primary.cardBackground}
            primaryMainColor={theme.palette.primary.main}
          />
        )}
        {myDayScreenSections.map((screenSection) => {
          if (
            screenSection.publishedSectionType ===
            SpacePublishedSectionType.LATEST_RECIPE_COLLECTIONS
          ) {
            return <MyDayLatestCollectionsSection key={1} />;
          }
          if (
            screenSection.publishedSectionType ===
            SpacePublishedSectionType.TODAYS_MEALS
          ) {
            return <MyDayTodaysMealsSection key={2} today={today} />;
          }
          if (
            screenSection.publishedSectionType ===
            SpacePublishedSectionType.TODAYS_LESSONS
          ) {
            return <MyDayTodaysLessonsSection key={3} today={today} />;
          }
          if (
            screenSection.publishedSectionType ===
            SpacePublishedSectionType.SHOPPING_LISTS
          ) {
            return <MyDayShoppingListsSection key={4} />;
          }
          return (
            <MyDayPublishedContentLaneSection
              key={screenSection.spaceContentLaneID}
              laneID={screenSection.spaceContentLaneID}
            />
          );
        })}
      </div>
    </div>
  );
};

export default MyDayView;
